"use server";

import { headers } from "next/headers";
import { z } from "zod";
import { randomBytes } from "node:crypto";
import { getDatabase } from "@/lib/db";
import { logger } from "@/lib/logger";
import { safeRequestId } from "@/lib/request-context";

export type ContactFormState = {
  status: "idle" | "error" | "success";
  message?: string;
  reference?: string;
  errors?: Record<string, string>;
};

const contactSchema = z.object({
  fullName: z
    .string()
    .trim()
    .min(2, "Lütfen adınızı ve soyadınızı girin.")
    .max(100, "Ad soyad en fazla 100 karakter olabilir."),
  email: z
    .string()
    .trim()
    .email("Geçerli bir e-posta adresi girin.")
    .max(150, "E-posta çok uzun."),
  phone: z
    .string()
    .trim()
    .min(10, "Lütfen geçerli bir telefon numarası girin.")
    .max(25, "Telefon numarası geçersiz."),
  subject: z
    .string()
    .trim()
    .min(2, "Lütfen bir konu seçin veya belirtin.")
    .max(120, "Konu çok uzun."),
  message: z
    .string()
    .trim()
    .min(10, "Mesajınız en az 10 karakter olmalıdır.")
    .max(3000, "Mesajınız en fazla 3000 karakter olabilir."),
  verificationField: z.string().optional(),
});

export async function submitContactAction(
  _previous: ContactFormState,
  formData: FormData,
): Promise<ContactFormState> {
  const requestHeaders = await headers();
  const requestId = safeRequestId(requestHeaders.get("x-request-id"));

  try {
    const rawData = Object.fromEntries(formData.entries());
    const parsed = contactSchema.parse(rawData);

    // Honeypot check
    if (parsed.verificationField && parsed.verificationField.trim().length > 0) {
      logger.warn("contact_form_honeypot_triggered", { requestId });
      return {
        status: "error",
        message: "Gönderim kabul edilmedi. Lütfen sayfayı yenileyip tekrar deneyin.",
      };
    }

    const reference = `CV-CON-${randomBytes(3).toString("hex").toUpperCase()}`;

    // Save message to database
    await getDatabase().contactMessage.create({
      data: {
        reference,
        fullName: parsed.fullName,
        email: parsed.email,
        phone: parsed.phone,
        subject: parsed.subject,
        message: parsed.message,
        status: "UNREAD",
      },
    });

    logger.info("contact_form_submitted", {
      requestId,
      reference,
      subject: parsed.subject,
      email: parsed.email,
    });

    return {
      status: "success",
      message: "Mesajınız başarıyla iletildi. Danışmanlarımız en kısa sürede sizinle iletişime geçecektir.",
      reference,
    };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of error.issues) {
        const fieldName = String(issue.path[0] || "");
        if (fieldName && !fieldErrors[fieldName]) {
          fieldErrors[fieldName] = issue.message;
        }
      }
      return {
        status: "error",
        message: "Lütfen form alanlarını kontrol edin.",
        errors: fieldErrors,
      };
    }

    logger.error("contact_form_failed", {
      requestId,
      errorType: error instanceof Error ? error.name : "UnknownError",
    });

    return {
      status: "error",
      message: "Mesajınız şu anda gönderilemedi. Lütfen daha sonra tekrar deneyin veya doğrudan telefon ile bize ulaşın.",
    };
  }
}
